<template>
    <div class="k_profile_menu_wrapper" @click.stop>
        <div
            ref="activatorRef"
            v-click-outside="handleClickOutside"
            @click="toggleMenu"
        >
            <slot name="activator" />
        </div>

        <Transition name="menu-fade">
            <div
                v-if="isOpen && enableBlur"
                class="k_profile_menu_backdrop"
                :class="{
                    'backdrop--show-header': showHeader,
                    'backdrop--show-footer': showFooter,
                }"
                @click="handleClose"
            />
        </Transition>

        <Transition name="menu-scale">
            <div
                v-if="isOpen"
                class="k_profile_menu bg-section"
                :style="menuStyle"
                @click="handleContentClick"
            >
                <slot name="content" />
            </div>
        </Transition>
    </div>
</template>

<script setup>
import { ref, computed, nextTick } from "vue";
import { useMobileTour } from "Admin/js/Mobile/Composables/useMobileTour";

const { isTourActive } = useMobileTour();

const props = defineProps({
    location: {
        type: String,
        default: "bottom start",
    },
    offset: {
        type: Array,
        default: () => [0, 8],
    },
    minWidth: {
        type: String,
        default: "280px",
    },
    enableBlur: {
        type: Boolean,
        default: true,
    },
    closeOnContentClick: {
        type: Boolean,
        default: true,
    },
    showHeader: {
        type: Boolean,
        default: false,
    },
    showFooter: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["update:modelValue", "onClose"]);

const isOpen = ref(false);
const activatorRef = ref(null);
const menuPosition = ref({});

const menuStyle = computed(() => ({
    minWidth: props.minWidth,
    ...menuPosition.value,
}));

const calculatePosition = () => {
    if (!activatorRef.value) return;

    const rect = activatorRef.value.getBoundingClientRect();
    const [offsetX, offsetY] = props.offset;
    const [vertical, horizontal] = props.location.split(" ");

    let position = {};

    if (vertical === "top") {
        position.bottom = `${window.innerHeight - rect.top + offsetY}px`;
    } else {
        position.top = `${rect.bottom + offsetY}px`;
    }

    if (horizontal === "start") {
        position.left = `${rect.left + offsetX}px`;
    } else if (horizontal === "end") {
        position.right = `${window.innerWidth - rect.right + offsetX}px`;
    } else {
        position.left = `${rect.left + offsetX}px`;
    }

    menuPosition.value = position;
};

const toggleMenu = async () => {
    isOpen.value = !isOpen.value;

    if (isOpen.value) {
        await nextTick();
        calculatePosition();
    }
};

const handleClose = () => {
    isOpen.value = false;
    emit("update:modelValue", false);
    emit("onClose");
};

const handleContentClick = () => {
    if (props.closeOnContentClick) {
        handleClose();
    }
};

const handleClickOutside = () => {
    if (isTourActive.value) {
        return;
    }
    handleClose();
};

defineExpose({
    isOpen,
});
</script>

<style lang="scss" scoped>
@import "../../../../sass/_theme-variables.sass";

.k_profile_menu_wrapper {
    position: relative;
    display: inline-block;
}

.k_profile_menu_backdrop {
    position: fixed;
    inset: 0;
    z-index: 100;
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    background-color: rgba(0, 0, 0, 0.2);
    pointer-events: auto;

    &.backdrop--show-header {
        top: 4rem;
    }

    &.backdrop--show-footer {
        bottom: 4rem;
    }
}

.k_profile_menu {
    position: fixed;
    z-index: 101;
    border: 1px solid $sectionBorder;
    border-radius: 0.5rem;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
    overflow: hidden;
}

.menu-fade-enter-active,
.menu-fade-leave-active {
    transition: opacity 200ms ease;
}

.menu-fade-enter-from,
.menu-fade-leave-to {
    opacity: 0;
}

.menu-scale-enter-active {
    animation: menu-scale-in 200ms ease-out;
}

.menu-scale-leave-active {
    animation: menu-scale-out 150ms ease-in;
}

@keyframes menu-scale-in {
    from {
        opacity: 0;
        transform: scale(0.95);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
}

@keyframes menu-scale-out {
    from {
        opacity: 1;
        transform: scale(1);
    }
    to {
        opacity: 0;
        transform: scale(0.95);
    }
}
</style>
